import { apiClient } from "./api-client";

export type ComplaintType = 'bug' | 'suggestion' | 'other' | 'avis' | 'plainte';

/** Statut interne renvoyé par l'API. */
export type ComplaintStatus = 'new' | 'in_progress' | 'resolved';

export interface ComplaintDto {
  name?: string;
  email?: string;
  phoneNumber?: string;
  type: ComplaintType;
  message: string;
  page?: string;
  /** Note optionnelle (1 à 5) accompagnant un avis. */
  rating?: number;
  /** Identifiant de la session / collecte concernée par le retour. */
  collectionContext?: string;
  /** Version de l'application desktop qui a émis le retour. */
  appVersion?: string;
  /** Horodatage côté client (retour rédigé hors-ligne). */
  clientTimestamp?: string;
}

export interface ComplaintItem {
  id: string;
  userId?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  type: string;
  message: string;
  page?: string;
  rating?: number | null;
  collectionContext?: string | null;
  appVersion?: string | null;
  clientTimestamp?: string | null;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string | null;
}

export interface ComplaintsResponse {
  data: ComplaintItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ComplaintsStats {
  total: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface ComplaintsFilters {
  type?: string;
  status?: string;
  /** Borne basse (ISO) sur createdAt. */
  dateFrom?: string;
  /** Borne haute (ISO) sur createdAt. */
  dateTo?: string;
  page?: number;
  limit?: number;
}

// Le back accepte les routes sous /complaints comme sous /feedback ; on garde
// /complaints pour ne pas casser l'existant.
const BASE = "/complaints";

export const complaintsApi = {
  submitComplaint: async (payload: ComplaintDto) => {
    const { data } = await apiClient.post(BASE, payload);
    return data;
  },

  getComplaints: async (
    filters?: ComplaintsFilters,
  ): Promise<ComplaintsResponse> => {
    const { data } = await apiClient.get(BASE, { params: filters });
    return data;
  },

  getComplaintsStats: async (): Promise<ComplaintsStats> => {
    const { data } = await apiClient.get(`${BASE}/stats`);
    return data;
  },

  getMyComplaints: async (): Promise<ComplaintItem[]> => {
    const { data } = await apiClient.get(`${BASE}/my`);
    return data;
  },

  /** Retours d'un utilisateur donné (self ou admin). */
  getUserComplaints: async (userId: string): Promise<ComplaintItem[]> => {
    const { data } = await apiClient.get(`${BASE}/user/${userId}`);
    return data;
  },

  updateComplaint: async (
    id: string,
    payload: { status?: string; priority?: string; resolvedAt?: Date }
  ) => {
    // PATCH (le back accepte PUT et PATCH) pour une sémantique de MAJ partielle.
    const { data } = await apiClient.patch(`${BASE}/${id}`, payload);
    return data;
  },

  deleteComplaint: async (id: string) => {
    const { data } = await apiClient.delete(`${BASE}/${id}`);
    return data;
  },

  replyComplaint: async (
    id: string,
    payload: { message: string; channel: 'email' | 'sms' }
  ) => {
    const { data } = await apiClient.post(`${BASE}/${id}/reply`, payload);
    return data;
  },
};
